import React, { useMemo, useEffect, useState } from 'react';
import { AnaliseCadastral, Negociacao } from '../../../../types';
import { ShieldCheck, AlertTriangle, Gauge, TrendingUp, User, MapPin, Phone, Mail, Calendar, Hash, Zap, CheckCircle2, MessageSquare, Shield, Repeat, KeyRound, Loader2, Clock, FileText, ExternalLink, Tag, Check } from 'lucide-react';
import { calcularMotorRisco, getClassificacaoColor, getScoreGaugeColor } from '../../../../src/lib/motorRisco';
import { useToast } from '../../../../src/contexts/ToastContext';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { getTermoValido } from '../../../../src/lib/termoValido';
import { podeLiberarAprovacaoComRessalvas } from '../../../../src/lib/departamentos';
import { supabase } from '../../../../src/lib/supabase';
import { logUsageEvent } from '../../../../src/lib/logger';
import { calcularConsumoUc } from '../../../../src/lib/analiseTecnicaConsumo';
import BiometriaPanel from '../components/BiometriaPanel';
import {
    pendenciasMotivoReprovacao,
    escolherMotivoPrincipal,
    alternarMotivoOutro,
    limparMotivoAoMudarDecisao,
    DECISAO_REPROVADO,
    type MotivoReprovacao,
} from '../../../../src/lib/motivosReprovacao';

/** Espelha `requiredChecks` de UcAnalysisModal — é o que decide se a UC sai de "Pendente". */
const CHECKS_OBRIGATORIOS_UC = ['atualizada', 'grupoB', 'beneficio', 'consumo500', 'possuiGD', 'injetando', 'mesmoTitular'] as const;

const LABEL_CHECK_UC: Record<string, string> = {
    atualizada: 'Fatura atualizada na Equatorial?',
    grupoB: 'A Unidade é Grupo B?',
    beneficio: 'Baixa Renda / Benefício?',
    consumo500: 'Consumo Compensável > 500 kWh?',
    possuiGD: 'Possui GD?',
    injetando: 'Terceiros injetando energia?',
    mesmoTitular: 'Titular da fatura confere com o termo?',
};

export const getPendenciasDocumentais = (docs: any, negociacao?: Negociacao, opts?: { tipo_cliente?: string; is_condominio?: boolean }): string[] => {
    const pendencias: string[] = [];
    const tipoCliente = opts?.tipo_cliente || negociacao?.tipo_cliente || 'PF';
    const isCondominio = opts?.is_condominio || false;
    const isPJ = tipoCliente === 'PJ';
    
    const termoKeys: Record<string, string> = {
        endereco_rua: 'Rua não confere',
        endereco_numero: 'Número não confere',
        endereco_quadra_lote: 'Quadra/Lote não confere',
        endereco_bairro: 'Bairro não confere',
        endereco_cidade: 'Cidade não confere',
        endereco_cep: 'CEP não confere',
        assinante_titular_poderes: 'Assinante não é titular/sem poderes',
        dados_associado_corretos: 'Dados do associado incorretos',
        desconto_fidelidade_corretos: 'Desconto/fidelidade incorretos no termo',
        ucs_conferem_faturas: 'UCs do termo divergentes das faturas',
        todas_ucs_titular: 'Nem todas as UCs pertencem ao titular',
        termo_assinado: 'Termo não assinado',
        autenticidade_assinaturas: 'Assinaturas não autênticas',
        desconto_concedido_certo: 'Desconto concedido incorreto',
        quantidade_unidades_certa: 'Quantidade de unidades incorreta'
    };

    if (docs) {
        if (docs.termo_adesao) {
            Object.entries(termoKeys).forEach(([key, label]) => {
                if (docs.termo_adesao[key] === false) pendencias.push(`Termo de Adesão: ${label}`);
            });
        }

        if (docs.rg_cnh?.presenca === 'Incomp.' || docs.rg_cnh?.presenca === 'Incompleto') pendencias.push('RG/CNH / Doc Rep.: Documento Incompleto');
        if (docs.rg_cnh?.legivel === false) pendencias.push('RG/CNH / Doc Rep.: Documento Ilegível');
        if (docs.rg_cnh?.ativo === false) pendencias.push('RG/CNH / Doc Rep.: Documento Inativo');

        if (docs.procuracao?.presenca === 'Incomp.' || docs.procuracao?.presenca === 'Incompleto') pendencias.push('Procuração: Documento Incompleto');
        if (docs.procuracao?.legivel === false) pendencias.push('Procuração: Documento Ilegível');
        if (docs.procuracao?.atual === false) pendencias.push('Procuração: Desatualizada');

        // PJ-only documents: Contrato Social, Cartão CNPJ
        if (isPJ && !isCondominio) {
            if (docs.contrato_social?.presenca === 'Incomp.' || docs.contrato_social?.presenca === 'Incompleto') pendencias.push('Contrato Social: Documento Incompleto');
            if (docs.contrato_social?.legivel === false) pendencias.push('Contrato Social: Documento Ilegível');
            if (docs.contrato_social?.cnpj_ativo === false) pendencias.push('Contrato Social: CNPJ Inativo');
        }

        // Cartão CNPJ: only for PJ or Condomínio
        if (isPJ || isCondominio) {
            if (docs.cartao_cnpj?.apresentado === false) pendencias.push('Cartão CNPJ: Não Apresentado');
            if (docs.cartao_cnpj?.status === 'Inativa') pendencias.push('Cartão CNPJ: Status Inativa');
        }

        // Condomínio-only documents: Ata de Constituição, Ata de Eleição
        if (isCondominio) {
            if (docs.ata_constituicao?.presenca === 'Incomp.' || docs.ata_constituicao?.presenca === 'Incompleto') pendencias.push('Ata de Constituição: Documento Incompleto');
            if (docs.ata_eleicao?.presenca === 'Incomp.' || docs.ata_eleicao?.presenca === 'Incompleto') pendencias.push('Ata de Eleição: Documento Incompleto');
        }

        // fatura_energia check removed — now handled at UC level (Step 1 - Relação de UCs)
    }

    if (negociacao?.unidades_consumidoras) {
        negociacao.unidades_consumidoras.forEach(uc => {
            const dte = (uc as any).dados_tecnicos_equatorial;
            const checks = dte?.checks;
            const ucLabel = (uc as any).outro_numero_uc ? `${uc.numero_uc} [${(uc as any).outro_numero_uc}]` : uc.numero_uc;
            if (checks) {
                if (checks.atualizada === false) pendencias.push(`UC ${ucLabel}: Fatura não está atualizada na Equatorial.`);
                if (checks.beneficio === true) pendencias.push(`UC ${ucLabel}: Conta classificada como Baixa Renda / Benefício.`);
                if (checks.injetando === true) pendencias.push(`UC ${ucLabel}: Terceiros injetando energia.`);
                if (checks.mesmoTitular === false) pendencias.push(`UC ${ucLabel}: Titular da fatura não confere com o termo de adesão.`);

                // GD com compensável < 1000 kWh — mesma regra da Análise Técnica
                if (checks.possuiGD === true) {
                    const history = dte?.historyTable;
                    if (history && history.length > 0) {
                        const { avgCompensavel } = calcularConsumoUc({
                            historico: history,
                            tipoLigacao: dte?.tipoFornecimento,
                            possuiGD: true,
                        });

                        if (avgCompensavel < 1000) {
                            pendencias.push(`UC ${ucLabel}: Possui GD e compensável (${avgCompensavel} kWh) abaixo de 1.000 kWh.`);
                        }
                    }
                }
            }
        });
    }

    return pendencias;
};

/**
 * Cross-step validation: returns array of { step, label, issues[] } for incomplete steps.
 * `motivosReprovacao`: lista de motivos (#6428). Sem ela (não carregou), o motivo
 * da reprovação não é cobrado — ver pendenciasMotivoReprovacao.
 */
export const getWizardStepValidation = (data: Partial<AnaliseCadastral>, negociacao?: Negociacao, motivosReprovacao?: MotivoReprovacao[] | null): { step: number; label: string; issues: string[] }[] => {
    const result: { step: number; label: string; issues: string[] }[] = [];

    // Step 2 — Identificação Geral
    const step2Issues: string[] = [];
    if (!negociacao?.whatsapp) step2Issues.push('Falta Telefone / WhatsApp do cliente');
    if (!negociacao?.email) step2Issues.push('Falta E-mail do cliente');
    if (!negociacao?.contato_financeiro && !negociacao?.cliente_nome) step2Issues.push('Falta Contato Financeiro');
    if (!negociacao?.email_financeiro && !negociacao?.email) step2Issues.push('Falta E-mail Financeiro');
    if (negociacao?.tipo_cliente === 'PJ' && !(negociacao as any)?.nome_representante_legal) step2Issues.push('Falta Representante/Responsável Legal');

    const pendenciasDocs = getPendenciasDocumentais(data.conferencia_docs, negociacao, { tipo_cliente: negociacao?.tipo_cliente, is_condominio: data.is_condominio || false });
    if (pendenciasDocs.length > 0) step2Issues.push(...pendenciasDocs);
    if (step2Issues.length > 0) result.push({ step: 2, label: 'Identificação Geral', issues: step2Issues });

    // Step 1 — Relação de UCs (Check if UCs are fully completed)
    const step1Issues: string[] = [];
    if (negociacao?.unidades_consumidoras) {
        negociacao.unidades_consumidoras.forEach(uc => {
            const dte = (uc as any).dados_tecnicos_equatorial;
            const ucLabel = (uc as any).outro_numero_uc ? `${uc.numero_uc} [${(uc as any).outro_numero_uc}]` : uc.numero_uc;
            if (!dte?.status || dte.status === 'Pendente') {
                // Dizer só "Pendente" obriga a abrir UC por UC pra descobrir o que
                // falta — foi o que travou a análise da Associação Energia, onde
                // faltava um único toggle em cada uma de duas UCs entre 60.
                const faltando = CHECKS_OBRIGATORIOS_UC
                    .filter(k => dte?.checks?.[k] === undefined || dte?.checks?.[k] === null)
                    .map(k => LABEL_CHECK_UC[k]);
                const detalhe = faltando.length > 0
                    ? ` Falta responder: ${faltando.join(', ')}.`
                    : '';
                step1Issues.push(`UC ${ucLabel}: Falta concluir a Análise Técnica (Pendente).${detalhe}`);
            }
        });
    } else {
        step1Issues.push('Nenhuma UC associada à negociação.');
    }
    if (step1Issues.length > 0) result.push({ step: 1, label: 'Relação de UCs', issues: step1Issues });

    // Step 3 — Análise de Crédito
    // A etapa é satisfeita pela consulta via API (serasa_experian_data) OU pelo
    // anexo do PDF do relatório Serasa (credito_relatorio_serasa_url). Ter o PDF
    // anexado não pode mais travar a Decisão Final (Etapa 4).
    const step3Issues: string[] = [];
    if (!data.serasa_experian_data && !data.credito_relatorio_serasa_url) {
        step3Issues.push('A consulta ao Serasa não foi realizada (consulte via API ou anexe o PDF do relatório).');
    }
    if (step3Issues.length > 0) result.push({ step: 3, label: 'Análise de Crédito', issues: step3Issues });

    // Step 4 — Parecer e Decisão
    const step4Issues: string[] = [];
    if (!data.decisao_final) step4Issues.push('Decisão final não selecionada');
    if (!data.risco_final) step4Issues.push('Risco final não selecionado');
    if (data.necessita_troca_titularidade === undefined) step4Issues.push('Informe se há necessidade de troca de titularidade da UC');
    step4Issues.push(...pendenciasMotivoReprovacao(data, motivosReprovacao));
    if (step4Issues.length > 0) result.push({ step: 4, label: 'Parecer e Decisão', issues: step4Issues });

    return result;
};

/**
 * Validação que efetivamente BLOQUEIA avançar/finalizar, já aplicando a liberação
 * de "Aprovado com Ressalvas".
 *
 * A liberação perdoa apenas pendência de REGRA DE NEGÓCIO — o que sai de
 * getPendenciasDocumentais (terceiros injetando, doc ilegível, GD com compensável
 * baixo etc.). Ela NÃO perdoa campo não preenchido: análise técnica de UC ainda
 * "Pendente", telefone/e-mail faltando, Serasa não consultado e os campos do
 * Passo 4 continuam obrigatórios.
 *
 * Usar esta função nos gates; usar getWizardStepValidation() para EXIBIR, porque
 * o banner deve seguir listando tudo, inclusive o que foi liberado.
 */
export const getBlockingStepValidation = (data: Partial<AnaliseCadastral>, negociacao?: Negociacao, motivosReprovacao?: MotivoReprovacao[] | null): { step: number; label: string; issues: string[] }[] => {
    const todas = getWizardStepValidation(data, negociacao, motivosReprovacao);

    const liberada = !!data.aprovacao_liberada_por && data.decisao_final === 'Aprovado com Ressalvas';
    if (!liberada) return todas;

    const perdoaveis = new Set(
        getPendenciasDocumentais(data.conferencia_docs, negociacao, {
            tipo_cliente: negociacao?.tipo_cliente,
            is_condominio: data.is_condominio || false,
        })
    );

    return todas
        .map(v => ({ ...v, issues: v.issues.filter(i => !perdoaveis.has(i)) }))
        .filter(v => v.issues.length > 0);
};

interface Step5Props {
    data: Partial<AnaliseCadastral>;
    onChange: (data: Partial<AnaliseCadastral>) => void;
    negociacao?: Negociacao;
    /** Lista de motivos de reprovação (#6428). `null` = carregando. */
    motivosReprovacao?: MotivoReprovacao[] | null;
    /** A lista não carregou: a escolha some e a reprovação segue só com o parecer. */
    erroMotivosReprovacao?: boolean;
}

export default function Step5Decisao({ data, onChange, negociacao, motivosReprovacao = null, erroMotivosReprovacao = false }: Step5Props) {
    const { addToast } = useToast();
    const { user, profile } = useAuth();
    const [gaugeAnimated, setGaugeAnimated] = useState(false);
    const [barsAnimated, setBarsAnimated] = useState(false);
    const [justificativaLiberacao, setJustificativaLiberacao] = useState('');
    const [liberando, setLiberando] = useState(false);

    useEffect(() => {
        const t1 = setTimeout(() => setGaugeAnimated(true), 200);
        const t2 = setTimeout(() => setBarsAnimated(true), 500);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange({ [e.target.name]: e.target.value });
    };

    const pendencias = useMemo(() => getPendenciasDocumentais(data.conferencia_docs, negociacao, { tipo_cliente: negociacao?.tipo_cliente, is_condominio: data.is_condominio || false }), [data.conferencia_docs, negociacao, data.is_condominio]);
    const validationIssuesParams = useMemo(() => getWizardStepValidation(data, negociacao).filter(v => v.step < 4), [data, negociacao]);
    const hasPreviousPendencias = validationIssuesParams.length > 0;
    const [showPendingWarning, setShowPendingWarning] = useState(false);

    // ── Liberação de "Aprovado com Ressalvas" sobre pendências das etapas 1-3 ──
    // Caso que motivou (Associação Energia GO, 1809): 51 UCs marcadas com
    // "terceiros injetando energia". Isso é esperado — a Vigor está assumindo a
    // injeção e as usinas antigas saem depois. A analista precisa MANTER as
    // reprovações registradas (é o controle de quem ainda tem terceiro injetando)
    // e ainda assim aprovar com ressalvas. Em vez de afrouxar a regra pra todo
    // mundo, a exceção é nomeada: quem liberou, quando e por quê.
    const aprovacaoLiberada = !!data.aprovacao_liberada_por;
    const podeLiberar = podeLiberarAprovacaoComRessalvas(profile);

    // Nome de quem liberou — pode ter sido outra pessoa, ou outra sessão.
    const [liberadoPorNome, setLiberadoPorNome] = useState('');
    useEffect(() => {
        const quem = data.aprovacao_liberada_por;
        if (!quem) { setLiberadoPorNome(''); return; }
        if (quem === user?.id) { setLiberadoPorNome(profile?.full_name || 'você'); return; }
        let ativo = true;
        supabase.from('profiles').select('full_name').eq('id', quem).maybeSingle()
            .then(({ data: p }) => { if (ativo) setLiberadoPorNome(p?.full_name || 'outro usuário'); });
        return () => { ativo = false; };
    }, [data.aprovacao_liberada_por, user?.id, profile?.full_name]);

    const handleLiberarAprovacao = async () => {
        const motivo = justificativaLiberacao.trim();
        if (!motivo) {
            addToast('Descreva o motivo da liberação antes de continuar.', 'error');
            return;
        }
        if (!data.id) {
            addToast('Salve a análise antes de liberar a aprovação.', 'error');
            return;
        }
        setLiberando(true);
        try {
            // Grava direto na tabela: o trigger trg_analise_liberacao_gate valida
            // departamento, autoria e justificativa no banco. Não depender do save
            // do wizard evita que a liberação se perca num autosave concorrente.
            const { data: row, error } = await supabase
                .from('analise_cadastral')
                .update({
                    aprovacao_liberada_por: user?.id,
                    aprovacao_liberada_justificativa: motivo,
                })
                .eq('id', data.id)
                .select('aprovacao_liberada_por, aprovacao_liberada_em, aprovacao_liberada_justificativa')
                .single();
            if (error) throw error;

            onChange({
                aprovacao_liberada_por: row.aprovacao_liberada_por,
                aprovacao_liberada_em: row.aprovacao_liberada_em,
                aprovacao_liberada_justificativa: row.aprovacao_liberada_justificativa,
            });

            await supabase.from('negociacao_logs').insert({
                negociacao_id: negociacao?.id,
                acao: `[APROVACAO_LIBERADA] ${motivo}`,
                detalhes: `${profile?.full_name || 'Usuário'} liberou "Aprovado com Ressalvas" apesar de ${validationIssuesParams.reduce((n, v) => n + v.issues.length, 0)} pendência(s) nas etapas anteriores. As pendências seguem registradas.`,
                user_id: user?.id,
            });

            setJustificativaLiberacao('');
            addToast('Aprovação com ressalvas liberada e registrada.', 'success');
            logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'dashboard', acao: 'liberar_aprovacao_ressalvas', origem: 'acao' });
        } catch (err: any) {
            addToast(`Não foi possível liberar: ${err?.message || 'erro desconhecido'}`, 'error');
        } finally {
            setLiberando(false);
        }
    };

    // Nota: aprovações com pendências nas etapas 1-3 são bloqueadas pela UI abaixo
    // (radio desabilitado + banner "Aprovação Bloqueada") e pela validação do
    // Avançar/Finalizar. Não sobrescrever data.decisao_final automaticamente aqui —
    // um efeito que trocava a decisão para 'Pendência Documental' em silêncio já
    // reprovou análise finalizada quando uma aba antiga do wizard remontou este step.

    const termoMaisRecente = getTermoValido(negociacao?.url_termo);
    const numTermo = termoMaisRecente?.numero_termo || '—';
    // Assinado com biometria mas ainda sem a confirmação do Autentique. Só marca
    // quando o campo veio explicitamente `false` — termos antigos, gravados antes
    // deste campo existir, ficam sem badge em vez de virarem falso alarme.
    const assinaturaAguardandoVerificacao =
        !!termoMaisRecente?.data_assinatura && (termoMaisRecente as any)?.assinatura_verificada === false;
    const ucs = negociacao?.unidades_consumidoras || [];
    const firstUc = ucs[0] as any;
    const consumoGlobal = negociacao?.consumo_medio_kwh ?? 0;
    const descontoTarifa = firstUc?.desconto_tarifa ?? 0;
    const descontoBandeira = firstUc?.desconto_bandeira ?? 0;
    const fidelidade = negociacao?.fidelidade || '—';
    const vendedor = negociacao?.responsavel?.full_name || '—';
    const dataNasc = firstUc?.data_nascimento_cliente || firstUc?.data_nascimento_titular;

    const calcIdade = (dn?: string) => {
        if (!dn) return null;
        const d = new Date(dn);
        const diff = Date.now() - d.getTime();
        return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
    };
    const idade = calcIdade(dataNasc);

    const resultado = useMemo(() => {
        const r = calcularMotorRisco(data.serasa_experian_data, data, negociacao);
        if (r.score !== data.motor_risco_score || r.classificacao !== data.motor_risco_classificacao) {
            onChange({ motor_risco_score: r.score, motor_risco_classificacao: r.classificacao });
        }
        return r;
    }, [data.serasa_experian_data, data.conferencia_docs, data.consumo_compensavel_kwh, negociacao?.consumo_medio_kwh]);

    const classColors = getClassificacaoColor(resultado.classificacao);
    const gaugeColor = getScoreGaugeColor(resultado.score);

    // Avalista cadastrado no caminho de recuperação de uma reprovação anterior
    // (ver migração 20260729190010_create_negociacao_avalistas.sql) — somente
    // leitura aqui, para o analista embasar a reanálise.
    const avalistaAtivo = negociacao?.avalistas?.find(a => a.status !== 'removido');
    // Serasa só existe em avalistas cadastrados até 28/08/2026 — a consulta saiu do
    // produto nessa data. Para os novos, o indicador de crédito passa a ser o
    // score_credito da Lemit, que já vinha sendo gravado no mesmo JSON.
    const avalistaSerasaScore = avalistaAtivo?.consulta_credito_json?.serasa?.score
        ?? avalistaAtivo?.consulta_credito_json?.serasa?.credito_score_serasa;
    const avalistaLemitRisco = avalistaAtivo?.consulta_credito_json?.lemit?.risco_credito?.score_credito;
    const avalistaLemitEndereco = avalistaAtivo?.consulta_credito_json?.lemit?.endereco
        ?? avalistaAtivo?.consulta_credito_json?.lemit?.enderecos?.[0];

    const decisionOptions = [
        { value: 'Aprovado', label: 'Aprovado', icon: CheckCircle2, desc: 'Cliente apto sem restrições', color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/15', border: 'border-green-200 dark:border-green-800/40', activeBg: 'bg-green-50 dark:bg-green-900/20', activeRing: 'ring-green-500/30' },
        { value: 'Aprovado com Ressalvas', label: 'Aprovado c/ Ressalvas', icon: AlertTriangle, desc: 'Aprovado com condições especiais', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/15', border: 'border-amber-200 dark:border-amber-800/40', activeBg: 'bg-amber-50 dark:bg-amber-900/20', activeRing: 'ring-amber-500/30' },
        { value: 'Exigir Garantia', label: 'Exigir Garantia', icon: Shield, desc: 'Requer garantia adicional', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/15', border: 'border-blue-200 dark:border-blue-800/40', activeBg: 'bg-blue-50 dark:bg-blue-900/20', activeRing: 'ring-blue-500/30' },
        { value: 'Pendência Documental', label: 'Pendência Documental', icon: Mail, desc: 'Documentação incompleta', color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/15', border: 'border-orange-200 dark:border-orange-800/40', activeBg: 'bg-orange-50 dark:bg-orange-900/20', activeRing: 'ring-orange-500/30' },
        { value: 'Reprovado', label: 'Reprovado', icon: AlertTriangle, desc: 'Não atende critérios mínimos', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/15', border: 'border-red-200 dark:border-red-800/40', activeBg: 'bg-red-50 dark:bg-red-900/20', activeRing: 'ring-red-500/30' },
    ];

    // ── Motivo da reprovação (#6428) ──
    const ehReprovado = data.decisao_final === DECISAO_REPROVADO;
    const outrosMotivos = data.motivos_reprovacao_outros ?? [];
    // Motivo desativado sai da escolha, mas continua aparecendo se já está marcado.
    const motivosEscolhiveis = (motivosReprovacao ?? []).filter(
        m => m.ativo || m.codigo === data.motivo_reprovacao || outrosMotivos.includes(m.codigo)
    );
    const motivoExigeDescricao = ehReprovado && motivosEscolhiveis.some(
        m => m.exige_descricao && (m.codigo === data.motivo_reprovacao || outrosMotivos.includes(m.codigo))
    );
    const faltaDescricaoDoMotivo = motivoExigeDescricao && !data.ressalvas_condicoes?.trim();

    const riskOptions = [
        { value: 'Excelente', color: '#10b981' },
        { value: 'Baixo', color: '#0ea5e9' },
        { value: 'Médio', color: '#f59e0b' },
        { value: 'Alto', color: '#ef4444' },
    ];

    const InfoItem = ({ icon: Icon, label, value, valueClass }: { icon: any; label: string; value: React.ReactNode; valueClass?: string }) => (
        <div className="space-y-1 group">
            <div className="flex items-center gap-1.5 text-slate-400">
                <Icon size={11} />
                <span className="text-[9px] font-bold uppercase tracking-[0.15em]">{label}</span>
            </div>
            <div className={`text-sm font-bold ${valueClass || 'text-slate-200'} transition-colors`}>{value || '—'}</div>
        </div>
    );

    return (
        <div className="space-y-6 animate-slide-up">
            {/* ── Ficha do Associado ── */}
            {negociacao && (
                <div className="bg-gradient-to-br from-[#212913] via-[#2a3318] to-[#212913] rounded-2xl border border-[#3a4420]/60 overflow-hidden shadow-xl shadow-primary/5">
                    {/* Top identity */}
                    <div className="flex items-center gap-4 px-6 py-5 border-b border-[#3a4420]/50">
                        <div className="p-3 rounded-xl bg-primary/20 text-primary border border-primary/20">
                            <User size={22} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-black text-white truncate font-poppins">{negociacao.cliente_nome}</h3>
                            <div className="flex items-center gap-2 text-sm text-slate-400 font-medium mt-0.5">
                                <span className="font-mono text-slate-300">{negociacao.cpf_cnpj || '—'}</span>
                                <span className="text-slate-600">•</span>
                                <span>Termo: <span className="font-mono text-secondary font-bold">{numTermo}</span></span>
                                {assinaturaAguardandoVerificacao && (
                                    <span
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px] font-bold uppercase tracking-wide"
                                        title="O cliente assinou, mas o Autentique ainda não concluiu a verificação biométrica (foto × documento). Enquanto isso, o PDF assinado não fica disponível para download. A análise pode seguir normalmente."
                                    >
                                        <Clock size={10} /> Assinatura aguardando verificação
                                    </span>
                                )}
                            </div>
                        </div>
                        {/* Consumo Global badge */}
                        <div className="hidden sm:flex flex-col items-end bg-primary/10 border border-primary/20 rounded-xl px-4 py-2">
                            <span className="text-[9px] font-bold text-white/90 uppercase tracking-widest">Consumo Global</span>
                            <span className="text-lg font-black text-secondary tabular-nums">{consumoGlobal.toLocaleString('pt-BR')} <span className="text-xs font-bold text-white/80">kWh</span></span>
                        </div>
                    </div>

                    {/* Data grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 px-6 py-5">
                        <InfoItem icon={MapPin} label="Localidade" value={ucs[0]?.cidade || '—'} />
                        <InfoItem icon={Phone} label="Contato" value={negociacao.whatsapp || '—'} />
                        <InfoItem
                            icon={Hash}
                            label="Descontos Concedidos"
                            value={
                                <span>
                                    Tarifa: <span className="text-emerald-400">{Math.round(descontoTarifa * 100)}%</span>
                                    {' | '}
                                    Band: <span className="text-emerald-400">{Math.round(descontoBandeira * 100)}%</span>
                                </span>
                            }
                        />
                        <InfoItem
                            icon={Hash}
                            label="UCs Relacionadas"
                            value={
                                <div className="flex flex-wrap gap-1.5">
                                    {ucs.length > 0 ? ucs.map((uc: any, i: number) => (
                                        <span key={i} className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-primary/15 border border-primary/20 rounded-lg font-mono text-xs text-slate-200 hover:bg-primary/25 transition-colors cursor-default">
                                            {uc.numero_uc}
                                            {uc.outro_numero_uc && (
                                                <span className="text-[10px] text-slate-400 bg-black/20 px-1 rounded font-bold">
                                                    {uc.outro_numero_uc}
                                                </span>
                                            )}
                                        </span>
                                    )) : '—'}
                                </div>
                            }
                        />
                        <InfoItem
                            icon={Calendar}
                            label="Nascimento & Idade"
                            value={dataNasc ? `${new Date(dataNasc).toLocaleDateString('pt-BR')} (${idade} anos)` : '—'}
                        />
                        <InfoItem icon={Mail} label="E-mail" value={negociacao.email || '—'} />
                        <InfoItem
                            icon={ShieldCheck}
                            label="Contrato & Comercial"
                            value={`Fid: ${fidelidade} | Vend: ${vendedor}`}
                        />
                        <InfoItem
                            icon={Zap}
                            label="Média de Consumo"
                            value={<span className="text-secondary">{consumoGlobal.toLocaleString('pt-BR')} kWh</span>}
                        />
                    </div>
                </div>
            )}

            {/* ── Verificação biométrica pendente ──
                Só aparece quando o cliente assinou e a biometria não voltou. É aqui
                que a análise trava: sem decidir a verificação manual do Autentique não
                existe PDF assinado nem selfie para conferir, e o documento fica
                bloqueado. Ver supabase/functions/biometria-termo. */}
            {assinaturaAguardandoVerificacao && negociacao?.id && (
                <BiometriaPanel negociacaoId={negociacao.id} numeroTermo={termoMaisRecente?.numero_termo} />
            )}

            {/* ── Motor de Risco ── */}
            <div className={`rounded-2xl border-2 overflow-hidden ${classColors.border} transition-all duration-500 shadow-sm`}>
                <div className="px-6 py-4 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-slate-700/50">
                        <Gauge size={16} className="text-slate-300" />
                    </div>
                    <div>
                        <span className="text-sm font-bold text-slate-200">Motor de Risco</span>
                        <span className="hidden sm:inline text-[10px] text-slate-500 font-medium ml-3">7 fatores ponderados</span>
                    </div>
                    <div className="ml-auto">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider ${classColors.text} ${classColors.bg} border ${classColors.border} shadow-sm`}>
                            {resultado.classificacao}
                        </span>
                    </div>
                </div>

                <div className={`${classColors.bg} p-6`}>
                    <div className="flex flex-col sm:flex-row items-center gap-8">
                        {/* Score Gauge */}
                        <div className="flex flex-col items-center gap-3">
                            <div className="relative w-28 h-28 flex items-center justify-center">
                                <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                                    <circle cx="60" cy="60" r="50" fill="none" strokeWidth="6" className="stroke-slate-200 dark:stroke-slate-700" />
                                    <circle
                                        cx="60" cy="60" r="50" fill="none"
                                        stroke={gaugeColor}
                                        strokeWidth="8"
                                        strokeLinecap="round"
                                        strokeDasharray={gaugeAnimated ? `${(resultado.score / 100) * 314} 314` : '0 314'}
                                        className="transition-all duration-1000 ease-out"
                                        style={{ filter: `drop-shadow(0 0 6px ${gaugeColor}50)` }}
                                    />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-4xl font-black text-slate-800 dark:text-slate-100 tabular-nums">{resultado.score}</span>
                                    <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">/ 100</span>
                                </div>
                            </div>
                        </div>

                        {/* Factor Breakdown */}
                        <div className="flex-1 w-full">
                            <div className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
                                <TrendingUp size={12} />
                                Breakdown dos Fatores
                            </div>
                            <div className="space-y-2.5">
                                {resultado.fatores.map((fator, idx) => (
                                    <div key={fator.nome} className="flex items-center gap-3 group" style={{ animationDelay: `${idx * 60}ms` }}>
                                        <div className="w-36 sm:w-44 text-xs font-semibold text-slate-600 dark:text-slate-300 truncate group-hover:text-primary transition-colors">{fator.nome}</div>
                                        <div className="flex-1 h-2.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full transition-all ease-out"
                                                style={{
                                                    width: barsAnimated ? `${fator.pontuacao}%` : '0%',
                                                    backgroundColor: getScoreGaugeColor(fator.pontuacao),
                                                    transitionDuration: `${600 + idx * 80}ms`,
                                                    boxShadow: `0 0 6px ${getScoreGaugeColor(fator.pontuacao)}30`,
                                                }}
                                            />
                                        </div>
                                        <div className="w-20 text-right text-xs font-bold text-slate-700 dark:text-slate-300 tabular-nums">
                                            {fator.pontuacao}<span className="text-slate-400 font-normal"> × {Math.round(fator.peso * 100)}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {avalistaAtivo && (
                <div className="bg-white dark:bg-slate-900/60 border border-teal-200/80 dark:border-teal-800/60 rounded-2xl shadow-sm overflow-hidden">
                    <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-teal-100 dark:border-teal-800/60 bg-teal-50/60 dark:bg-teal-900/10">
                        <div className="p-1.5 bg-teal-100 dark:bg-teal-900/40 rounded-lg">
                            <Shield size={14} className="text-teal-700 dark:text-teal-400" />
                        </div>
                        <h4 className="text-sm font-bold text-teal-800 dark:text-teal-300">Avalista (caminho de recuperação)</h4>
                    </div>
                    <div className="p-4 space-y-2 text-sm">
                        <p className="text-slate-700 dark:text-slate-300">
                            <strong>{avalistaAtivo.nome_completo}</strong> — CPF {avalistaAtivo.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Status: {avalistaAtivo.status}</p>
                        {avalistaSerasaScore != null && (
                            <p className="text-xs text-slate-500 dark:text-slate-400">Score Serasa do avalista: {avalistaSerasaScore}</p>
                        )}
                        {avalistaSerasaScore == null && avalistaLemitRisco && (
                            <p className="text-xs text-slate-500 dark:text-slate-400">Risco de crédito do avalista (Lemit): {avalistaLemitRisco}</p>
                        )}
                        {avalistaLemitEndereco && (
                            <p className="text-xs text-slate-500 dark:text-slate-400">Endereço (Lemit): {typeof avalistaLemitEndereco === 'string' ? avalistaLemitEndereco : JSON.stringify(avalistaLemitEndereco)}</p>
                        )}
                        {avalistaAtivo.titular_assinou_em && (
                            <p className="text-xs text-slate-500 dark:text-slate-400">Titular assinou em: {new Date(avalistaAtivo.titular_assinou_em).toLocaleDateString('pt-BR')}</p>
                        )}
                        {avalistaAtivo.avalista_assinou_em && (
                            <p className="text-xs text-slate-500 dark:text-slate-400">Avalista assinou em: {new Date(avalistaAtivo.avalista_assinou_em).toLocaleDateString('pt-BR')}</p>
                        )}
                        {/* Só com as duas assinaturas: antes disso o assinado.pdf do Autentique não é o termo final */}
                        {avalistaAtivo.termo_url_pdf && avalistaAtivo.titular_assinou_em && avalistaAtivo.avalista_assinou_em && (
                            <a
                                href={avalistaAtivo.termo_url_pdf}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-teal-700 dark:text-teal-300 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 rounded"
                            >
                                <FileText size={13} aria-hidden="true" />
                                Ver termo de avalista assinado
                                <ExternalLink size={12} aria-hidden="true" />
                            </a>
                        )}
                    </div>
                </div>
            )}

            {/* ── Decision Grid ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Decisão Final — Card Radio */}
                <div className="bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden flex flex-col">
                    <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                        <div className="p-1.5 bg-primary/8 dark:bg-primary/15 rounded-lg">
                            <ShieldCheck size={14} className="text-primary" />
                        </div>
                        <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Decisão Final</h4>
                    </div>
                    <div className="p-4 space-y-2 flex-1">
                        {hasPreviousPendencias && (
                            <div className={`mb-4 p-4 rounded-xl border ${aprovacaoLiberada
                                ? 'border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-900/10'
                                : 'border-red-200 bg-red-50 dark:border-red-800/50 dark:bg-red-900/10'}`}>
                                <div className={`flex items-start gap-3 ${aprovacaoLiberada ? 'text-amber-700 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                                    <AlertTriangle className="shrink-0 mt-0.5" size={16} />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-bold">
                                            {aprovacaoLiberada ? 'Aprovação Liberada com Ressalvas' : 'Aprovação Bloqueada'}
                                        </p>
                                        <p className="text-xs font-medium mt-1">
                                            {aprovacaoLiberada
                                                ? 'As pendências abaixo continuam registradas e fazem parte do parecer — a liberação não as apaga:'
                                                : 'A análise apresenta pendências nas etapas anteriores que impedem a aprovação:'}
                                        </p>
                                        <div className="mt-2 text-xs opacity-90 space-y-2 font-semibold ml-1">
                                            {validationIssuesParams.map((vi, i) => (
                                                <div key={i}>
                                                    <span className="underline uppercase tracking-wide">Etapa {vi.step} - {vi.label}:</span>
                                                    <ul className="list-disc list-inside ml-2 mt-0.5">
                                                        {vi.issues.map((iss, j) => <li key={j}>{iss}</li>)}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>

                                        {aprovacaoLiberada ? (
                                            <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-800/50 text-xs">
                                                <p className="font-bold flex items-center gap-1.5">
                                                    <KeyRound size={12} /> Liberado por {liberadoPorNome}
                                                    {data.aprovacao_liberada_em && ` em ${new Date(data.aprovacao_liberada_em).toLocaleString('pt-BR')}`}
                                                </p>
                                                <p className="mt-1 font-medium whitespace-pre-wrap opacity-90">
                                                    Motivo: {data.aprovacao_liberada_justificativa}
                                                </p>
                                            </div>
                                        ) : podeLiberar ? (
                                            <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-800/50">
                                                <p className="text-xs font-bold mb-2">
                                                    Se as pendências acima são esperadas neste caso, você pode liberar a aprovação com ressalvas — elas continuam registradas.
                                                </p>
                                                <textarea
                                                    value={justificativaLiberacao}
                                                    onChange={(e) => setJustificativaLiberacao(e.target.value)}
                                                    rows={2}
                                                    className="w-full rounded-lg border border-red-200 dark:border-red-800/50 bg-white dark:bg-slate-800/60 px-3 py-2 text-xs text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none resize-none placeholder:text-red-300"
                                                    placeholder="Justifique por que estas pendências não impedem a aprovação..."
                                                />
                                                <button
                                                    onClick={handleLiberarAprovacao}
                                                    disabled={!justificativaLiberacao.trim() || liberando}
                                                    className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-xs font-bold rounded-lg hover:bg-amber-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {liberando ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />}
                                                    {liberando ? 'Liberando...' : 'Liberar aprovação com ressalvas'}
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        )}
                        {decisionOptions.map((opt) => {
                            const isSelected = data.decisao_final === opt.value;
                            const isBlockedApproval = opt.value === 'Aprovado' || opt.value === 'Aprovado com Ressalvas';
                            // A liberação destrava SÓ "Aprovado com Ressalvas". Uma análise com
                            // pendências não pode virar aprovação limpa — a ressalva tem que ficar
                            // registrada no nome da decisão.
                            const liberadoParaEsta = aprovacaoLiberada && opt.value === 'Aprovado com Ressalvas';
                            const isDisabled = hasPreviousPendencias && isBlockedApproval && !liberadoParaEsta;
                            const Icon = opt.icon;
                            return (
                                <label
                                    key={opt.value}
                                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all duration-200 ${
                                        isSelected
                                            ? `${opt.activeBg} ${opt.border} ring-2 ${opt.activeRing} shadow-sm`
                                            : isDisabled ? 'border-transparent opacity-40 cursor-not-allowed grayscale' : 'border-transparent hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:border-slate-200 dark:hover:border-slate-700/60 cursor-pointer'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="decisao_final"
                                        value={opt.value}
                                        checked={isSelected}
                                        onChange={(e) => {
                                            if (isDisabled) {
                                                e.preventDefault();
                                                addToast('Existem pendências nas etapas anteriores! Verifique o box de "Aprovação Bloqueada" acima.', 'error');
                                            } else {
                                                // Fora de "Reprovado" o motivo deixa de existir.
                                                onChange({ decisao_final: opt.value, ...limparMotivoAoMudarDecisao(opt.value, data) });
                                            }
                                        }}
                                        className="sr-only"
                                    />
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${isSelected ? opt.bg : 'bg-slate-100 dark:bg-slate-800'}`}>
                                        <Icon size={15} className={isSelected ? opt.color : 'text-slate-400'} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <span className={`text-sm font-bold block transition-colors ${isSelected ? opt.color : 'text-slate-700 dark:text-slate-300'}`}>{opt.label}</span>
                                        <span className="text-[10px] text-slate-400 font-medium">{opt.desc}</span>
                                    </div>
                                    {isSelected && (
                                        <CheckCircle2 size={18} className={`${opt.color} shrink-0 animate-bounce-check`} />
                                    )}
                                </label>
                            );
                        })}
                    </div>
                </div>

                {/* Risco Final */}
                <div className="bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
                    <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                        <div className="p-1.5 bg-primary/8 dark:bg-primary/15 rounded-lg">
                            <AlertTriangle size={14} className="text-primary" />
                        </div>
                        <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Risco Final Atribuído</h4>
                    </div>
                    <div className="p-4 space-y-2">
                        {riskOptions.map((opt) => {
                            const isSelected = data.risco_final === opt.value;
                            return (
                                <label
                                    key={opt.value}
                                    className={`flex items-center gap-3 p-3.5 rounded-xl cursor-pointer border transition-all duration-200 ${
                                        isSelected
                                            ? 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/40 ring-2 ring-offset-1 shadow-sm'
                                            : 'border-transparent hover:bg-slate-50 dark:hover:bg-slate-800/40'
                                    }`}
                                    style={isSelected ? { boxShadow: `0 0 0 2px ${opt.color}40` } : {}}
                                >
                                    <input
                                        type="radio"
                                        name="risco_final"
                                        value={opt.value}
                                        checked={isSelected}
                                        onChange={() => onChange({ risco_final: opt.value })}
                                        className="sr-only"
                                    />
                                    <div
                                        className="w-3 h-3 rounded-full shrink-0 transition-all duration-300"
                                        style={{
                                            backgroundColor: isSelected ? opt.color : '#cbd5e1',
                                            boxShadow: isSelected ? `0 0 8px ${opt.color}60` : 'none',
                                        }}
                                    />
                                    <span className={`text-sm font-bold transition-colors ${isSelected ? 'text-slate-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-400'}`}>{opt.value}</span>
                                    {isSelected && <CheckCircle2 size={16} style={{ color: opt.color }} className="ml-auto shrink-0 animate-bounce-check" />}
                                </label>
                            );
                        })}

                        {/* Engine Suggestion */}
                        {resultado.classificacao && (
                            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                                <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/40 rounded-xl">
                                    <div>
                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.12em] mb-1">Sugestão do Motor</div>
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black ${classColors.text} ${classColors.bg} border ${classColors.border}`}>
                                            <Gauge size={11} />
                                            {resultado.classificacao} ({resultado.score} pts)
                                        </span>
                                    </div>
                                    <div className="relative w-10 h-10">
                                        <svg viewBox="0 0 40 40" className="-rotate-90">
                                            <circle cx="20" cy="20" r="16" fill="none" strokeWidth="3" className="stroke-slate-200 dark:stroke-slate-700" />
                                            <circle cx="20" cy="20" r="16" fill="none" stroke={gaugeColor} strokeWidth="3" strokeLinecap="round"
                                                strokeDasharray={gaugeAnimated ? `${(resultado.score / 100) * 100.5} 100.5` : '0 100.5'}
                                                className="transition-all duration-1000 ease-out"
                                            />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Motivo da Reprovação (#6428) ── */}
            {ehReprovado && (
                <div className="bg-white dark:bg-slate-900/60 border border-red-200/80 dark:border-red-800/50 rounded-2xl shadow-sm overflow-hidden">
                    <div className="flex flex-wrap items-center gap-2.5 px-5 py-3.5 border-b border-red-100 dark:border-red-800/40 bg-red-50/60 dark:bg-red-900/10">
                        <div className="p-1.5 bg-red-100 dark:bg-red-900/40 rounded-lg">
                            <Tag size={14} className="text-red-600 dark:text-red-400" />
                        </div>
                        <h4 className="text-sm font-bold text-red-800 dark:text-red-300">Motivo da Reprovação</h4>
                        {data.motivo_reprovacao_retroativo && (
                            <span
                                className="ml-auto px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-wide"
                                title="Esta análise é anterior à lista de motivos. O motivo foi classificado depois, a partir do texto do parecer."
                            >
                                Classificado depois, pelo parecer
                            </span>
                        )}
                    </div>
                    <div className="p-5 space-y-5">
                        {motivosReprovacao === null ? (
                            <p className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                <Loader2 size={13} className="animate-spin" /> Carregando os motivos...
                            </p>
                        ) : erroMotivosReprovacao || motivosEscolhiveis.length === 0 ? (
                            <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                                Não foi possível carregar a lista de motivos. Descreva o motivo da reprovação no campo Ressalvas / Condições abaixo.
                            </p>
                        ) : (
                            <>
                                <fieldset>
                                    <legend className="text-xs font-bold text-slate-600 dark:text-slate-300 mb-2">
                                        Motivo principal <span className="text-red-500">*</span>
                                    </legend>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {motivosEscolhiveis.map(motivo => {
                                            const selecionado = data.motivo_reprovacao === motivo.codigo;
                                            return (
                                                <label
                                                    key={motivo.codigo}
                                                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all duration-200 focus-within:ring-2 focus-within:ring-red-400/50 ${
                                                        selecionado
                                                            ? 'border-red-300 dark:border-red-700/60 bg-red-50 dark:bg-red-900/20 ring-2 ring-red-500/25'
                                                            : 'border-slate-200 dark:border-slate-700/60 hover:bg-slate-50 dark:hover:bg-slate-800/40'
                                                    }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="motivo_reprovacao"
                                                        value={motivo.codigo}
                                                        checked={selecionado}
                                                        onChange={() => onChange(escolherMotivoPrincipal(motivo.codigo, outrosMotivos))}
                                                        className="sr-only"
                                                    />
                                                    <span
                                                        aria-hidden="true"
                                                        className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${selecionado ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
                                                    >
                                                        {selecionado && <span className="w-2 h-2 rounded-full bg-red-500" />}
                                                    </span>
                                                    <span className="min-w-0">
                                                        <span className={`block text-sm font-bold ${selecionado ? 'text-red-700 dark:text-red-300' : 'text-slate-700 dark:text-slate-200'}`}>
                                                            {motivo.rotulo}
                                                        </span>
                                                        <span className="block text-[11px] leading-snug text-slate-500 dark:text-slate-400">{motivo.descricao}</span>
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </fieldset>

                                <fieldset>
                                    <legend className="text-xs font-bold text-slate-600 dark:text-slate-300 mb-2">
                                        Também pesou na decisão <span className="font-medium text-slate-400 dark:text-slate-500">(opcional, pode marcar vários)</span>
                                    </legend>
                                    <div className="flex flex-wrap gap-2">
                                        {motivosEscolhiveis.filter(m => m.codigo !== data.motivo_reprovacao).map(motivo => {
                                            const marcado = outrosMotivos.includes(motivo.codigo);
                                            return (
                                                <label
                                                    key={motivo.codigo}
                                                    title={motivo.descricao}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-red-400/50 ${
                                                        marcado
                                                            ? 'bg-red-600 border-red-600 text-white'
                                                            : 'border-slate-200 dark:border-slate-700/60 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40'
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={marcado}
                                                        onChange={() => onChange({
                                                            motivos_reprovacao_outros: alternarMotivoOutro(motivo.codigo, data.motivo_reprovacao, outrosMotivos, motivosReprovacao ?? []),
                                                        })}
                                                        className="sr-only"
                                                    />
                                                    {marcado && <Check size={12} aria-hidden="true" />}
                                                    {motivo.rotulo}
                                                </label>
                                            );
                                        })}
                                    </div>
                                </fieldset>

                                {motivoExigeDescricao && (
                                    <p className={`text-xs font-medium ${faltaDescricaoDoMotivo ? 'text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'}`}>
                                        Com o motivo "Outro", a descrição no campo Ressalvas / Condições abaixo é obrigatória.
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ── Observations ── */}
            <div className="bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
                <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                    <div className="p-1.5 bg-primary/8 dark:bg-primary/15 rounded-lg">
                        <MessageSquare size={14} className="text-primary" />
                    </div>
                    <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Ressalvas / Condições / Garantias</h4>
                    {faltaDescricaoDoMotivo && (
                        <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-red-600 dark:text-red-400">Obrigatório</span>
                    )}
                </div>
                <div className="p-5">
                    <textarea
                        name="ressalvas_condicoes"
                        value={data.ressalvas_condicoes || ''}
                        onChange={handleChange}
                        rows={5}
                        className="w-full rounded-xl border border-slate-200 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/40 px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 resize-none shadow-sm transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 hover:border-slate-300 dark:hover:border-slate-600"
                        placeholder="Descreva aqui as ressalvas, condições para aprovação ou garantias que devem ser exigidas..."
                    />
                </div>
            </div>

            {/* ── Troca de Titularidade ── */}
            <div className="bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
                <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                    <div className="p-1.5 bg-primary/8 dark:bg-primary/15 rounded-lg">
                        <Repeat size={14} className="text-primary" />
                    </div>
                    <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Troca de Titularidade</h4>
                </div>
                <div className="p-5 space-y-2">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Há necessidade de trocar a titularidade da UC junto à distribuidora?</p>
                    <div className="grid grid-cols-2 gap-3">
                        {(['Sim', 'Não'] as const).map((opt) => {
                            const value = opt === 'Sim';
                            const isSelected = data.necessita_troca_titularidade === value;
                            return (
                                <label
                                    key={opt}
                                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border cursor-pointer transition-all duration-200 ${
                                        isSelected
                                            ? 'border-primary/40 bg-primary/8 dark:bg-primary/15 ring-2 ring-primary/30 shadow-sm'
                                            : 'border-slate-200 dark:border-slate-700/60 hover:bg-slate-50 dark:hover:bg-slate-800/40'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="necessita_troca_titularidade"
                                        checked={isSelected}
                                        onChange={() => onChange({ necessita_troca_titularidade: value })}
                                        className="sr-only"
                                    />
                                    <span className={`text-sm font-bold ${isSelected ? 'text-primary' : 'text-slate-600 dark:text-slate-300'}`}>{opt}</span>
                                    {isSelected && <CheckCircle2 size={16} className="text-primary shrink-0" />}
                                </label>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
